import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { keepPreviousData } from '@tanstack/react-query';

import { Segmentation } from '@playq/octopus2-analytics';
import { AppEntityContentResponse } from '@playq/octopus2-apps';
import { GenericFailure } from '@playq/services-shared';
import { PermModule } from '@playq/octopus2-auth';

import { IUseQueryOptionsExtended, useEitherQuery } from '/api/service-hooks';
import { services2 } from '/api/services2';
import { appToolkit } from '/store';
import { useModulePermissions } from '/hooks/useModulePermissions';

export const segmentRetrieveKeys: unknown[] = ['segment', 'retrieve'];

const emptyArray: [] = [];

export const useSegmentationRetrieve = (
  version?: number,
  options?: IUseQueryOptionsExtended<AppEntityContentResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const { canRead } = useModulePermissions(PermModule.AnalyticsSegmentation);

  const res = useEitherQuery(
    segmentRetrieveKeys.concat(appID, version),
    () => {
      if (appID) {
        return services2.appSegmentsService.retrieve(appID, version);
      }
    },
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled: options?.enabled !== false && canRead,
    }
  );

  const segmentation = useMemo(() => {
    if (res.data !== undefined) {
      try {
        return new Segmentation(JSON.parse(res.data.entity.content));
      } catch (e) {
        console.error(e);
        return undefined;
      }
    }
  }, [res.data]);

  return {
    segmentation,
    globals: segmentation?.globals || emptyArray,
    segments: segmentation?.segments || emptyArray,
    version: res.data?.entity.version,
    lastVersion: res.data?.lastVersion,
    ...res,
  };
};
